import React, { useState, useEffect } from 'react'

export interface BillingPortalProps {
  customerId: string
  onSuccess?: (data: any) => void
  onError?: (error: Error) => void
  className?: string
  style?: React.CSSProperties
}

export function BillingPortal({ 
  customerId, 
  onSuccess, 
  onError, 
  className, 
  style 
}: BillingPortalProps) {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [portalUrl, setPortalUrl] = useState<string | null>(null)

  const createPortalSession = async () => {
    setLoading(true)
    setError(null)

    try {
      // In a real implementation, you'd call your API to create a billing portal session
      const response = await fetch('/api/billing/portal', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ customerId })
      })

      if (!response.ok) {
        throw new Error('Failed to create billing portal session')
      }

      const data = await response.json()
      setPortalUrl(data.url)
      onSuccess?.(data)
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to create billing portal')
      setError(error.message)
      onError?.(error)
    } finally {
      setLoading(false)
    }
  }

  const handleOpenPortal = () => {
    if (portalUrl) {
      window.open(portalUrl, '_blank')
    } else {
      createPortalSession()
    }
  }

  return (
    <div className={`billing-portal ${className || ''}`} style={style}>
      <div className="billing-portal-header">
        <h3>Billing Portal</h3>
        <p>Manage your subscription and billing information</p>
      </div>

      {error && (
        <div className="billing-portal-error">
          <p>{error}</p>
        </div>
      )}

      <div className="billing-portal-actions">
        <button
          className="billing-portal-button"
          onClick={handleOpenPortal}
          disabled={loading}
        >
          {loading ? 'Opening...' : 'Open Billing Portal'}
        </button>
      </div>

      <style>{`
        .billing-portal {
          max-width: 400px;
          margin: 0 auto;
          padding: 2rem;
          border: 1px solid #e1e5e9;
          border-radius: 8px;
          background: white;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }

        .billing-portal-header {
          text-align: center;
          margin-bottom: 2rem;
        }

        .billing-portal-header h3 {
          margin: 0 0 0.5rem 0;
          color: #1a1a1a;
          font-size: 1.25rem;
          font-weight: 600;
        }

        .billing-portal-header p {
          margin: 0;
          color: #6b7280;
          font-size: 0.875rem;
        }

        .billing-portal-error {
          background: #fef2f2;
          border: 1px solid #fecaca;
          border-radius: 6px;
          padding: 0.75rem;
          margin-bottom: 1rem;
        }

        .billing-portal-error p {
          margin: 0;
          color: #dc2626;
          font-size: 0.875rem;
        }

        .billing-portal-actions {
          display: flex;
          justify-content: center;
        }

        .billing-portal-button {
          padding: 0.75rem 1.5rem;
          border: 1px solid #3b82f6;
          border-radius: 6px;
          background: #3b82f6;
          color: white;
          font-size: 0.875rem;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .billing-portal-button:hover:not(:disabled) {
          background: #2563eb;
          border-color: #2563eb;
        }

        .billing-portal-button:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
      `}</style>
    </div>
  )
}
